import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { SessionTypeEnum } from '~/utils/constants/session';
import { getQueryParams } from '~/utils/url';
import {
  getExpiredSessionIds,
  removeOrphanedSessionsStorage,
  removeSessionStorage,
  validateExternalSessionQuery
} from '~/utils/session';

const checkAppUpdateAvailability = async () => {
  try {
    const { updateAvailability } = await AppUpdate.getAppUpdateInfo();

    if (updateAvailability > AppUpdateAvailability.UPDATE_NOT_AVAILABLE) {
      await AppUpdate.openAppStore();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error checking update', error);
  }
};

export default defineNuxtPlugin({
  hooks: {
    async 'app:created'() {
      const { isNativePlatform } = usePlatform();

      const router = useRouter();

      const route = useRoute();

      const apiStore = useApiStore();

      const authStore = useAuthStore();

      const sessionStore = useSessionStore();

      // Validate and reset apiBase if it doesn't match any available options
      apiStore.validateAndResetApiBase();

      const validateQueryAndRedirectIfValid = async (url: URLOpenListenerEvent['url'] | Location['href']) => {
        if (authStore.isLoggedIn) {
          const query = getQueryParams(url);

          const isValidQuery = validateExternalSessionQuery(query);

          if (isValidQuery) {
            // This is workaround, because createExternalSession function is not working properly here
            await router.replace({ name: 'external-create-session', query });
          }
        }
      };

      if (authStore.isLoggedIn) {
        try {
          await authStore.getAppData();

          if (!isNativePlatform) {
            const allSessions = await sessionStore.fetchAllSessions(SessionTypeEnum.Desktop);

            removeOrphanedSessionsStorage(allSessions.map(({ id }) => id));
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching app data', error);
        }
      }

      if (isNativePlatform) {
        await checkAppUpdateAvailability();

        await App.addListener('appStateChange', async ({ isActive }) => {
          if (isActive) {
            await checkAppUpdateAvailability();
          }
        });

        await App.addListener('appUrlOpen', ({ url }: URLOpenListenerEvent) => {
          if (url) {
            setTimeout(async () => {
              await validateQueryAndRedirectIfValid(url);
            }, 1000);
          }
        });

        // Remove expired sessions files
        const expiredSessions = await getExpiredSessionIds();

        if (expiredSessions.length) {
          for (const sessionId of expiredSessions) {
            await deleteSessionFiles(sessionId);

            removeSessionStorage(sessionId);
          }
        }

        await SplashScreen.hide();
      } else if (Object.keys(route.query).length > 0) {
        // Handle query parameters on desktop (browser)
        setTimeout(async () => {
          await validateQueryAndRedirectIfValid(window.location.href);
        }, 500);
      }
    }
  }
});
